import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Login } from '@trovex/auth/react';
import { useAuth } from './useAuth.js';
import { useAdmin } from './useAdmin.js';
import AdminGate from './components/AdminGate.jsx';
import Layout from './components/Layout.jsx';
import Dashboard from './pages/Dashboard.jsx';

const Sends = lazy(() => import('./pages/Sends.jsx'));
const Composer = lazy(() => import('./pages/Composer.jsx'));
const Audiences = lazy(() => import('./pages/Audiences.jsx'));
const Contacts = lazy(() => import('./pages/Contacts.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
const Guide = lazy(() => import('./pages/Guide.jsx'));
const ApprovalPage = lazy(() => import('./pages/ApprovalPage.jsx'));
const Approvals = lazy(() => import('./pages/Approvals.jsx'));

export default function App() {
  const { user, loading: authLoading, loginWithTrovexId, requestEmailCode, verifyEmailCode , trackPageView } = useAuth();
  // THE INSTANCE useTrovexAuth ALREADY BUILT, not a second one. That hook
  // constructs the plugin and passes trackPageView through this app's own
  // wrapper (it spreads `...shared`); a second at module scope meant two
  // instances, each registering window.onerror, so every client error was
  // reported twice. The hook exposes the function and calls it for nobody,
  // which is the whole reason this line has to exist.
  const telemetryPath = useLocation().pathname;
  useEffect(() => { trackPageView(telemetryPath); }, [telemetryPath, trackPageView]);


  const { isAdmin, loading: adminLoading } = useAdmin(user?.email);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <Login
        appName="Broadcast"
        tagline="Email campaigns to customers and contractors. Enter your Trovex email and we&rsquo;ll send you a 6-digit code to sign in."
        icon={(
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        )}
        onTrovexIdLogin={loginWithTrovexId}
        onRequestCode={requestEmailCode}
        onVerifyCode={verifyEmailCode}
      />
    );
  }

  return (
    <Suspense fallback={<div className="p-6 text-gray-500">Loading…</div>}>
      <Routes>
        <Route path="/approve/:sendId" element={<ApprovalPage />} />
        <Route
          path="/*"
          element={
            <AdminGate isAdmin={isAdmin} loading={adminLoading}>
                <Routes>
                  <Route element={<Layout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="sends" element={<Sends />} />
                    <Route path="sends/:sendId" element={<Composer />} />
                    <Route path="approvals" element={<Approvals />} />
                    <Route path="audiences" element={<Audiences />} />
                    <Route path="contacts" element={<Contacts />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="guide" element={<Guide />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
            </AdminGate>
          }
        />
      </Routes>
    </Suspense>
  );
}
